import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { LOCALSTORAGEKEYS } from '../../_constants/localStorageKeys';
import {
    GeoApiParams,
    GeoDisplayNames,
    GeoLevel,
    GeoMapEntry,
    GeoPermission,
    StoredGeography,
} from '../../_models/geography.model';
import { LocalService } from '../_storage/local.service';

@Injectable({
    providedIn: 'root'
})
export class GeographyService {
    private permissionsSubject = new BehaviorSubject<GeoPermission[]>([]);
    private activeSubject = new BehaviorSubject<GeoPermission | null>(null);
    private geoMapRequest$: Observable<GeoMapEntry[]> | null = null;

    readonly permissions$ = this.permissionsSubject.asObservable();
    readonly active$ = this.activeSubject.asObservable();

    constructor(
        private http: HttpClient,
        private localService: LocalService
    ) { }

    init(
        userSub: string,
        resourceAccess: Record<string, { roles?: string[] }>
    ): Observable<GeoPermission[]> {
        const restored = this.restore(userSub);
        if (restored) {
            this.apply(restored);
            return of(restored);
        }
        return this.loadGeoMap().pipe(
            map((geoMap) => this.build(resourceAccess, geoMap)),
            tap((permissions) => {
                this.apply(permissions);
                this.persist(userSub, permissions);
            }),
            catchError(() => {
                this.apply([]);
                return of([] as GeoPermission[]);
            })
        );
    }

    initMock(): Observable<GeoPermission[]> {
        const demo: GeoPermission = {
            level: 'block',
            role: 'block-manager-ib',
            clientKey: 'B-000000',
            stateId: 0,
            stateCode: '',
            stateName: 'Telangana',
            stateShortCode: 'TG',
            districtId: 0,
            districtName: 'Hyderabad',
            blockId: 0,
            blockName: 'Amberpet',
        };
        this.apply([demo]);
        return of([demo]);
    }

    get permissions(): GeoPermission[] {
        return this.permissionsSubject.getValue();
    }

    get active(): GeoPermission | null {
        return this.activeSubject.getValue();
    }

    setActive(permission: GeoPermission): void {
        this.activeSubject.next(permission);
    }

    getByLevel(level: GeoLevel, role?: string): GeoPermission[] {
        return this.permissions.filter(
            (p) => p.level === level && (!role || p.role === role)
        );
    }

    get apiParams(): GeoApiParams {
        const active = this.active;
        if (!active) {
            return {};
        }
        const params: GeoApiParams = {};
        if (active.stateId != null) {
            params.stateId = active.stateId;
        }
        if (active.districtId != null) {
            params.districtId = active.districtId;
        }
        if (active.blockId != null) {
            params.blockId = active.blockId;
        }
        return params;
    }

    get displayNames(): GeoDisplayNames {
        const active = this.active;
        return {
            state: active?.stateName || '',
            district: active?.districtName || '',
            block: active?.blockName || '',
        };
    }

    get tenantId(): string {
        const shortCode = this.active?.stateShortCode;
        return shortCode ? `dynamic_lokos_${shortCode}` : 'dynamic_lokos';
    }

    clear(): void {
        this.permissionsSubject.next([]);
        this.activeSubject.next(null);
        this.geoMapRequest$ = null;
        this.localService.remove(LOCALSTORAGEKEYS.GEOGRAPHY);
    }

    private apply(permissions: GeoPermission[]): void {
        this.permissionsSubject.next(permissions);
        const preferred = permissions.find(
            (p) => p.role === 'block-manager-ib'
        );
        if (preferred) {
            this.activeSubject.next(preferred);
            return;
        }
        const order: GeoLevel[] = ['block', 'district', 'state', 'national'];
        for (const level of order) {
            const match = permissions.find((p) => p.level === level);
            if (match) {
                this.activeSubject.next(match);
                return;
            }
        }
        this.activeSubject.next(null);
    }

    getStateShortCode(
        stateId: string | number | null | undefined,
    ): Observable<string> {
        const sid = Number(`${stateId ?? ''}`.trim());
        if (!Number.isFinite(sid)) {
            return of('');
        }
        const active = this.active;
        if (active?.stateId === sid && active?.stateShortCode) {
            return of(`${active.stateShortCode}`.toUpperCase());
        }
        return this.loadGeoMap().pipe(
            map((geoMap) => {
                const entry = geoMap.find(
                    (g) => this.toNumericId(g.state_id) === sid,
                );
                return entry ? `${entry.short_code || ''}`.toUpperCase() : '';
            }),
            catchError(() => of('')),
        );
    }

    private loadGeoMap(): Observable<GeoMapEntry[]> {
        if (!this.geoMapRequest$) {
            const url = `${environment.keyclockCdnUrl}geo-map.json`;
            this.geoMapRequest$ = this.http
                .get<GeoMapEntry[]>(url)
                .pipe(shareReplay(1));
        }
        return this.geoMapRequest$;
    }

    private build(
        resourceAccess: Record<string, { roles?: string[] }>,
        geoMap: GeoMapEntry[]
    ): GeoPermission[] {
        const permissions: GeoPermission[] = [];
        const excluded = ['account', 'broker', 'realm-management'];
        Object.keys(resourceAccess || {}).forEach((key) => {
            if (excluded.includes(key)) {
                return;
            }
            const roleList = resourceAccess[key]?.roles || [];
            roleList.forEach((role) => {
                const permission = this.resolveKey(key, role, geoMap);
                if (permission) {
                    permissions.push(permission);
                }
            });
        });
        return permissions;
    }

    private resolveKey(
        key: string,
        role: string,
        geoMap: GeoMapEntry[]
    ): GeoPermission | null {
        const prefix = key.split('-')[0];
        switch (prefix) {
            case 'B': {
                const entry = geoMap.find((g) => g.block_id === key);
                return entry
                    ? this.toPermission('block', role, key, entry, true, true)
                    : null;
            }
            case 'D': {
                const entry = geoMap.find((g) => g.district_id === key);
                return entry
                    ? this.toPermission('district', role, key, entry, true, false)
                    : null;
            }
            case 'S': {
                const code = key.split('-')[1];
                const entry = geoMap.find(
                    (g) =>
                        g.state_id.split('-')[1].padStart(2, '0') === code
                );
                return entry
                    ? this.toPermission('state', role, key, entry, false, false)
                    : null;
            }
            case 'N': {
                return {
                    level: 'national',
                    role,
                    clientKey: key,
                    stateId: null,
                    stateCode: '',
                    stateName: '',
                    stateShortCode: '',
                    districtId: null,
                    districtName: '',
                    blockId: null,
                    blockName: '',
                };
            }
            default:
                return null;
        }
    }

    private toPermission(
        level: GeoLevel,
        role: string,
        clientKey: string,
        entry: GeoMapEntry,
        includeDistrict: boolean,
        includeBlock: boolean
    ): GeoPermission {
        return {
            level,
            role,
            clientKey,
            stateId: this.toNumericId(entry.state_id),
            stateCode: entry.state_code,
            stateName: entry.state_name,
            stateShortCode: entry.short_code,
            districtId: includeDistrict
                ? this.toNumericId(entry.district_id)
                : null,
            districtName: includeDistrict ? entry.district_name : '',
            blockId: includeBlock ? this.toNumericId(entry.block_id) : null,
            blockName: includeBlock ? entry.block_name : '',
        };
    }

    private toNumericId(code: string): number | null {
        const part = `${code || ''}`.split('-')[1];
        const value = Number(part);
        return Number.isFinite(value) ? value : null;
    }

    private persist(userSub: string, permissions: GeoPermission[]): void {
        const payload: StoredGeography = { sub: userSub, permissions };
        this.localService.set(LOCALSTORAGEKEYS.GEOGRAPHY, payload);
    }

    private restore(userSub: string): GeoPermission[] | null {
        const stored = this.localService.get<StoredGeography>(
            LOCALSTORAGEKEYS.GEOGRAPHY
        );
        if (!stored || stored.sub !== userSub) {
            return null;
        }
        const valid =
            Array.isArray(stored.permissions) && stored.permissions.length
                ? stored.permissions
                : null;
        if (!valid) {
            return null;
        }
        const stale = valid.some(
            (p) => p.level !== 'national' && p.stateCode === undefined
        );
        return stale ? null : valid;
    }
}
